import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Stack,
    Box,
    Button,
    Divider,
    Paper,
    Chip,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Lucide from 'lucide-react';

const CardStyled = styled(Card)(({ theme, color }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderTop: `4px solid ${color || theme.palette.primary.main}`,
    transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
    '&:hover': {
        boxShadow: theme.shadows[6],
        transform: 'translateY(-4px)',
    },
}));

const KV = ({ label, value, valueProps = {} }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" component="div">
            {label}
        </Typography>
        <Typography
            variant="body2"
            sx={{ wordBreak: 'break-word' }}
            {...valueProps}
        >
            {value ?? '—'}
        </Typography>
    </Box>
);

export default function ProtocolMicrobi({ protocol, onShowJson }) {
    const p = protocol;
    const theme = useTheme();

    return (
        <CardStyled color={p.color}>
            <CardContent
                sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
            >
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1}
                    >
                        <Typography
                            variant="h6"
                            component="h3"
                            sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}
                        >
                            {p.name}
                        </Typography>
                        <Chip
                            label={p.active ? 'Ativo' : 'Inativo'}
                            color={p.active ? 'success' : 'default'}
                            size="small"
                            sx={{ mt: 0.5 }}
                        />
                    </Stack>

                    <Stack spacing={1.5}>
                        <KV label="Tipo de Protocolo" value={p.type} />
                        <KV
                            label="ID do Protocolo"
                            value={p.id}
                            valueProps={{
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                            }}
                        />
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 'bold', mb: 1.5 }}
                        >
                            Análises Vinculadas
                        </Typography>
                        <Stack spacing={1}>
                            {p.analysis?.length > 0 ? (
                                p.analysis.map((a) => (
                                    <Paper
                                        key={a.id}
                                        variant="outlined"
                                        sx={{
                                            p: 1,
                                            bgcolor: 'action.hover',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1.5}
                                            alignItems="center"
                                        >
                                            <Lucide.Microscope
                                                size={20}
                                                color={
                                                    a.color ||
                                                    theme.palette.primary.main
                                                }
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                {a.name}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                ))
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Nenhuma análise.
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Stack>

                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}
                >
                    <Button
                        variant="text"
                        size="small"
                        startIcon={<Lucide.Code size={20} />}
                        onClick={() => onShowJson(p)}
                    >
                        Ver JSON
                    </Button>
                </Stack>
            </CardContent>
        </CardStyled>
    );
}
